/**
 * Auth модуль — Phase 2 multi-user Wave 1.
 *
 * До Wave 1 был Basic Auth + sessionStorage. Теперь — JWT в HttpOnly cookie,
 * выставляется backend'ом через /api/auth/login, читается всеми последующими
 * fetch с `credentials: 'include'`.
 *
 * Публичный API:
 *   - login(login, password) → user object | throws
 *   - logout() → void
 *   - me() → user object | null
 *   - getCurrentUser() → cached user (синхронный)
 *   - showLoginModal() / hideLoginModal() — DOM helpers
 *   - canDo(actionCode) → boolean (Wave 4 data-driven hide layer)
 */

const API_BASE = window.CCPM_CONFIG?.apiBase ?? 'http://localhost:8001';

let _currentUser = null;
// Phase 2 Wave 4: subset matrix для текущего пользователя — ключ "action_code" → boolean.
// Owner всегда true (заполняется при me/login). Подгружается из /api/auth/me
// для не-owner — используется в canDo() для UI hide layer.
let _permissionsMatrix = {};

export async function login(loginName, password) {
  const r = await fetch(`${API_BASE}/api/auth/login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ login: loginName, password }),
  });
  if (!r.ok) {
    const data = await r.json().catch(() => ({}));
    const err = new Error(data.detail || 'Ошибка входа');
    err.status = r.status;
    throw err;
  }
  _currentUser = await r.json();
  await _loadPermissionsMatrix();
  return _currentUser;
}

export async function logout() {
  try {
    await fetch(`${API_BASE}/api/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    });
  } catch {
    /* ignore network errors — cookie может быть уже expired */
  }
  _currentUser = null;
  _permissionsMatrix = {};
}

export async function me() {
  try {
    const r = await fetch(`${API_BASE}/api/auth/me`, {
      credentials: 'include',
    });
    if (!r.ok) {
      _currentUser = null;
      _permissionsMatrix = {};
      return null;
    }
    _currentUser = await r.json();
    await _loadPermissionsMatrix();
    return _currentUser;
  } catch {
    _currentUser = null;
    _permissionsMatrix = {};
    return null;
  }
}

async function _loadPermissionsMatrix() {
  // Owner — всегда всё разрешено, грузить matrix не нужно (canDo возвращает true).
  if (!_currentUser || _currentUser.role === 'owner') {
    _permissionsMatrix = {};
    return;
  }
  try {
    const r = await fetch(`${API_BASE}/api/auth/permissions`, {
      credentials: 'include',
    });
    if (r.ok) {
      _permissionsMatrix = await r.json();
    } else {
      _permissionsMatrix = {};
    }
  } catch {
    _permissionsMatrix = {};
  }
}

export function getCurrentUser() {
  return _currentUser;
}

/**
 * Phase 2 Wave 4: data-driven permission check для UI.
 * Owner — всегда true. Иначе — лукап в _permissionsMatrix (подгружается из
 * /api/auth/permissions при login/me).
 *
 * Для scoped actions (project.update и т.п.) для manager/user возвращает
 * true если matrix разрешает — но per-project проверка backend всё равно
 * блокирует чужие проекты. UI hide-layer не различает scoped/unscoped:
 * для viewer task.read=true но он реально видит только assigned. Это OK —
 * его UI просто не показывает чужие проекты в list.
 */
export function canDo(actionCode) {
  if (!_currentUser) return false;
  if (_currentUser.role === 'owner') return true;
  return _permissionsMatrix[actionCode] === true;
}

/* DOM helpers — backward compat с index.html (login-modal markup) */

export function showLoginModal() {
  const modal = document.getElementById('login-modal');
  const app = document.getElementById('app');
  if (modal) modal.classList.remove('hidden');
  if (app) app.classList.add('hidden');
  const errEl = document.getElementById('login-error');
  if (errEl) errEl.style.display = 'none';
}

export function hideLoginModal() {
  const modal = document.getElementById('login-modal');
  const app = document.getElementById('app');
  if (modal) modal.classList.add('hidden');
  if (app) app.classList.remove('hidden');
}

/* Legacy exports — больше не используются, но оставлены чтобы не сломать импорты
   до полного рефактора всех views. Возвращают no-op / always-true. */
export function isLoggedIn() {
  return !!_currentUser;
}

export function getAuthHeader() {
  // Cookie передаётся автоматом через credentials: 'include' — header больше не нужен.
  return {};
}

export function saveCredentials() {
  /* no-op — cookie ставит backend через Set-Cookie */
}

export function clearCredentials() {
  _currentUser = null;
}
